import _extends from "@babel/runtime/helpers/esm/extends";
import _defineProperty from "@babel/runtime/helpers/esm/defineProperty";
import _objectWithoutProperties from "@babel/runtime/helpers/esm/objectWithoutProperties";
var _excluded = ["children", "className", "anchor", "variant", "bgColor", "style", "anchorDirection", "originDirection", "open", "jss", "onClose", "transition", "fitAnchor", "menuWidth", "backdrop"],
    _excluded2 = ["list", "menu", "root"];
import React, { Fragment } from 'react';
import PropTypes from 'prop-types';
import cn from 'classnames';
import Popper from "../Popper";
import createStyles from "../createStyles";
import Paper from "../Paper";
import { AnchorDirectionType, OriginDirectionType } from "../enums";
import ClickOutsideListener from "../ClickOutsideListener";
import { white } from "../theme/colors";
export var useStyles = createStyles(function (theme) {
  return {
    root: {
      width: '100%',
      overflowX: 'hidden',
      overflowY: 'auto',
      padding: 0,
      marginBottom: 0,
      listStyleType: 'none'
    },
    menu: {
      maxHeight: 260
    },
    list: {
      overflowY: 'hidden',
      maxHeight: 'auto'
    },
    enter: {
      opacity: 0.01,
      transform: 'scaleY(0)'
    },
    enterActive: {
      transform: 'scaleY(1)',
      opacity: 1,
      transition: theme.transition.create('all', {
        duration: 200
      })
    },
    enterDone: {
      transform: 'scaleY(1)',
      opacity: 1
    },
    exit: {
      opacity: 1,
      transform: 'scaleY(1)'
    },
    exitDone: {
      opacity: 0,
      transform: 'scaleY(0)'
    },
    exitActive: {
      transform: 'scaleY(0)',
      opacity: 0.01,
      transition: theme.transition.create('all', {
        duration: 200
      })
    }
  };
}, {
  name: 'Menu'
});
var Menu = /*#__PURE__*/React.forwardRef(function Menu(_ref, ref) {
  var children = _ref.children,
      _ref$className = _ref.className,
      className = _ref$className === void 0 ? '' : _ref$className,
      anchor = _ref.anchor,
      _ref$variant = _ref.variant,
      variant = _ref$variant === void 0 ? 'list' : _ref$variant,
      _ref$bgColor = _ref.bgColor,
      bgColor = _ref$bgColor === void 0 ? white : _ref$bgColor,
      _ref$style = _ref.style,
      style = _ref$style === void 0 ? {} : _ref$style,
      _ref$anchorDirection = _ref.anchorDirection,
      anchorDirection = _ref$anchorDirection === void 0 ? AnchorDirectionType.Start : _ref$anchorDirection,
      _ref$originDirection = _ref.originDirection,
      originDirection = _ref$originDirection === void 0 ? OriginDirectionType.Bottom : _ref$originDirection,
      _ref$open = _ref.open,
      open = _ref$open === void 0 ? false : _ref$open,
      _ref$jss = _ref.jss,
      jss = _ref$jss === void 0 ? {} : _ref$jss,
      onClose = _ref.onClose,
      transition = _ref.transition,
      fitAnchor = _ref.fitAnchor,
      menuWidth = _ref.menuWidth,
      _ref$backdrop = _ref.backdrop,
      backdrop = _ref$backdrop === void 0 ? true : _ref$backdrop,
      props = _objectWithoutProperties(_ref, _excluded);

  var classes = useStyles({
    jss: jss
  });

  var list = classes.list,
      menu = classes.menu,
      root = classes.root,
      defaultMenuTransition = _objectWithoutProperties(classes, _excluded2);

  var Component = variant === 'menu' ? Popper : Fragment;
  var wrapperProps = {};

  if (variant === 'menu') {
    wrapperProps = {
      width: menuWidth,
      anchor: anchor,
      transition: transition || defaultMenuTransition,
      trigger: open,
      fitAnchor: fitAnchor,
      backdrop: backdrop,
      anchorDirection: anchorDirection,
      originDirection: originDirection
    };
  }

  return /*#__PURE__*/React.createElement(Component, wrapperProps, /*#__PURE__*/React.createElement(ClickOutsideListener, {
    delay: 250,
    onClickOutside: onClose
  }, /*#__PURE__*/React.createElement(Paper, {
    elevation: 2,
    bgColor: bgColor
  }, /*#__PURE__*/React.createElement("ul", _extends({}, props, {
    role: "listbox",
    tabIndex: -1,
    ref: ref,
    className: cn(classes.root, _defineProperty({}, classes[variant], variant), className),
    style: style
  }), children))));
});
Menu.propTypes = {
  /** Indicator whether or not to open menu when variant = menu */
  open: PropTypes.bool,

  /** Anchor element to append menu type Menu */
  anchor: PropTypes.oneOfType([PropTypes.node, PropTypes.func, PropTypes.shape()]),

  /** Child items in the menu component */
  children: PropTypes.node.isRequired,

  /** Additional class for root level */
  className: PropTypes.string,

  /** Additional style for root level */
  style: PropTypes.shape(),

  /** variant to set the menu style - values - [menu, dropdown] */
  variant: PropTypes.oneOf(['menu', 'list']),

  /* Menu transition prop for css transition */
  transition: PropTypes.oneOfType([PropTypes.shape({
    appear: PropTypes.string,
    appearActive: PropTypes.string,
    enter: PropTypes.string.isRequired,
    enterActive: PropTypes.string.isRequired,
    enterDone: PropTypes.string,
    exitActive: PropTypes.string.isRequired,
    exit: PropTypes.string.isRequired,
    exitDone: PropTypes.string
  }), PropTypes.string]),

  /** Background color to set to the menu */
  bgColor: PropTypes.string,

  /** Direction to open the content relative to anchor (center, end ,start) */
  anchorDirection: PropTypes.oneOf([AnchorDirectionType.Center, AnchorDirectionType.Start, AnchorDirectionType.End]),

  /** Direction to open the content relative to origin (Top bottom left right) */
  originDirection: PropTypes.oneOf([OriginDirectionType.Top, OriginDirectionType.Bottom, OriginDirectionType.Left, OriginDirectionType.Right]),

  /** In case variant is menu - set the menu content width manually - by default it will resolve to the larger of the input width or the content width */
  menuWidth: PropTypes.number,

  /** Control the backdrop for the popper element in variant = menu, defaults to true */
  backdrop: PropTypes.bool
};
Menu.displayName = 'Menu';
export default Menu;